/**
 * Canonical country-profile enums.
 *
 * These enums are used by the country-profiles subsystem and rankings pages.
 * They are client-only types (not in the GraphQL schema).
 *
 * `src/types/countryProfileEnums.ts` re-exports from here so that app code
 * can import them without a package prefix when convenient.
 *
 * Only `LocationMetadatumLevel` and `GroupLevel` are exported from the
 * `@atlas-int/data` package barrel — the rest stay internal.
 */

import { ProductLevel } from "./enums";

// -- Location / product metadata enums (originally from graphql/types.ts) --

export enum LocationMetadatumLevel {
  country = "country",
  subregion = "subregion",
  region = "region",
  world = "world",
}

/**
 * @deprecated Use `ProductLevel` from `./enums` instead.
 * Kept as an alias so country-profiles code continues to compile.
 */
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ProductMetadatumLevel = ProductLevel;
/** @deprecated Use `ProductLevel` from `./enums` instead. */
export type ProductMetadatumLevel = ProductLevel;

export enum GroupLevel {
  Region = "region",
  Subregion = "subregion",
}

// -- From src/country-profiles/graphQL/types/shared.ts --

/**
 * Client-side ProductClass with backward-compatible values.
 *
 * shared.ts used `HS92 = "HS"` while the generated schema uses `Hs92 = "HS92"`.
 * Country-profiles code sends these values to a *different* API than the
 * explore-tool GraphQL API, so the string values here must match what that
 * API expects.  Keep this enum for country-profiles; the explore tool should
 * use the `ProductClass` from `./enums.ts`.
 */
export enum CPProductClass {
  HS92 = "HS",
  HS12 = "HS12",
  HS22 = "HS22",
  SITC = "SITC",
  /**
   * @deprecated Alias for `HS92`. Both resolve to `"HS"`.
   * Prefer `CPProductClass.HS92` for clarity. Kept for backward compatibility
   * with code that used the shorter `ProductClass.HS` from the legacy shared.ts.
   */
  HS = "HS",
}

export enum CPTradeFlow {
  Gross = "Gross",
  Net = "Net",
}

export enum CPTradeDirection {
  export = "export",
  import = "import",
}

export enum CPProductLevel {
  section = "section",
  twoDigit = "twoDigit",
  fourDigit = "fourDigit",
  sixDigit = "sixDigit",
}

export enum ProductType {
  Goods = "Goods",
  Service = "Service",
}

export enum QueryLevel {
  Location = "location",
  Group = "group",
}

export enum TreeMapType {
  CPY_C = "CPY_C",
  CPY_P = "CPY_P",
  CCPY_CP = "CCPY_CP",
  CCPY_CC = "CCPY_CC",
  CCY_C = "CCY_C",
}

/**
 * @deprecated Use `TreeMapType` instead — `DataFacet` and `TreeMapType` were
 * identical enums. This alias is kept for backward compatibility.
 */
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const DataFacet = TreeMapType;
/** @deprecated Use `TreeMapType` instead. */
export type DataFacet = TreeMapType;

export enum DecileClassification {
  Top = "Top",
  Second = "Second",
  Third = "Third",
  Fourth = "Fourth",
  Fifth = "Fifth",
  Sixth = "Sixth",
  Seventh = "Seventh",
  Eighth = "Eighth",
  Ninth = "Ninth",
  Last = "Last",
  Empty = "Empty",
  NA = "NA",
}

export enum IncomeClassification {
  High = "High",
  UpperMiddle = "UpperMiddle",
  LowerMiddle = "LowerMiddle",
  Low = "Low",
}

export enum NewProductsIncomeGrowthComments {
  LargeEnough = "LargeEnough",
  TooSmall = "TooSmall",
}

export enum GrowthProjectionClassification {
  rapid = "rapid",
  moderate = "moderate",
  slow = "slow",
}

export enum DiversificationGrade {
  APlus = "APlus",
  A = "A",
  B = "B",
  C = "C",
  D = "D",
  DMinus = "DMinus",
}

export enum MarketShareMainSectorDirection {
  rising = "rising",
  falling = "falling",
  stagnant = "stagnant",
}

export enum StructuralTransformationStep {
  NotStarted = "NotStarted",
  TextilesOnly = "TextilesOnly",
  ElectronicsOnly = "ElectronicsOnly",
  MachineryOnly = "MachineryOnly",
  Completed = "Completed",
}

export enum StructuralTransformationDirection {
  risen = "risen",
  fallen = "fallen",
  stagnated = "stagnated",
}

export enum NewProductsComments {
  TooFew = "TooFew",
  Sufficient = "Sufficient",
}

export enum NewProductsComplexityStatusGrowthPrediction {
  More = "More",
  Same = "Same",
  Less = "Less",
}

export enum COIClassification {
  low = "low",
  medium = "medium",
  high = "high",
}

export enum PolicyRecommendation {
  ParsimoniousIndustrial = "ParsimoniousIndustrial",
  StrategicBets = "StrategicBets",
  LightTouch = "LightTouch",
  TechFrontier = "TechFrontier",
}

export enum GrowthProjectionRelativeToIncome {
  More = "More",
  Less = "Less",
  Same = "Same",
  ModeratelyMore = "ModeratelyMore",
  ModeratelyLess = "ModeratelyLess",
}

export enum GrowthProjectionPercentileClassification {
  TopDecile = "TopDecile",
  TopQuartile = "TopQuartile",
  TopHalf = "TopHalf",
  BottomHalf = "BottomHalf",
}

export enum GDPPCConstantCAGRRegionalDifference {
  Above = "Above",
  InLine = "InLine",
  Below = "Below",
}

export enum ExportValueGrowthClassification {
  Troubling = "Troubling",
  Mixed = "Mixed",
  Static = "Static",
  Promising = "Promising",
}

export enum LookBackYearRange {
  ThreeYears = "ThreeYears",
  FiveYears = "FiveYears",
  TenYears = "TenYears",
  FifteenYears = "FifteenYears",
}
